import { Injectable, ApplicationRef } from '@angular/core';

import 'rxjs/Rx';
import { Observable, Subject, BehaviorSubject } from 'rxjs/Rx';

import { Product } from '../../_models/product';
import { CartProduct } from '../../_models/cart-product';
import { Size } from '../../_models/size';
import { Download } from '../../_models/download';
import { QueryService } from '../../shared/query.service';
import { CardToPrint } from '../../_models/card-to-print';
import { ApplicationNotificationService } from '../../shared/application-notification.service';
import {AuthenticationService} from "../../shared/auth/authentication.service";


@Injectable()
export class CartService {
  public cartProducts: CartProduct[] = [];
  public cartProductsBehaviorSubject: BehaviorSubject<CartProduct[]> = new BehaviorSubject([]);
  public sizes: Map<string, Size[]> = new Map<string, Size[]>();
  public downloads: Download[] = [];
  public validateForm = false;
  public loading = false;
  public showDownloads = false;


  constructor(private authService: AuthenticationService, private queryService: QueryService, private appNotificationService: ApplicationNotificationService) {
    this.authService.logoutBehaviorSubject.subscribe((logout) => { if (logout) {this.resetCart();}})
  }

  resetCart(): void {
    this.updateCart([]);
    this.validateForm = false;
  }

  updateCart(newCart: CartProduct[]) {
    this.cartProducts = newCart;
    this.cartProductsBehaviorSubject.next(this.cartProducts);
  }


  getTemplatesAndSizes(product: Product): Observable<Size[]> {
    const url = '/cards/product_cards/?product_id=' + product.id;
    return this.queryService.getQuery(url).map(result => {
      this.sizes.set(product.id, result);
      return result;
    });
  }

  getSizesIfNotAvailable(product: Product): void {
    if (!this.sizes.has(product.id)) {
      this.getTemplatesAndSizes(product).subscribe();
    }
  }

  productInCart(product: Product): boolean {
    for (const key in this.cartProducts) {
      if (this.cartProducts[key].product_id === product.id) {
        return true;
      }
    }
    return false;
  }

  flipProduct(product: Product): void {
    if (this.productInCart(product)) {
      this.removeProduct(product);
    } else {
      this.addProduct(product);
    }
  }

  updateCartProduct(oldCp: CartProduct, updatedCp: CartProduct): void {
    // Loop over all cartproducts in our array, look which one is the same as our 'old cartproduct'
    for (const cp of this.cartProducts) {
      if (cp === oldCp) {
        // If we have a hit, get the index
        const index = this.cartProducts.indexOf(cp);
        const list = this.cartProducts;
        // Now replace our old cartproduct with our new cartproduct
        list.splice(index, 1, updatedCp);
        this.updateCart(list);
      }
    }
  }

  addProduct(newProduct: Product): void {
    this.getSizesIfNotAvailable(newProduct);
    // If the product isn't yet in our list, add it
    if (!this.productInCart(newProduct)) {
      const cp = new CartProduct(newProduct);
      this.cartProducts.push(cp);
      this.updateCart(this.cartProducts);
    }
  }

  addProductByID(productID: number): void {
    this.queryService.getProduct(productID).subscribe(result => {
      this.addProduct(result);
    });
  }

  addProductByCardToPrint(cardToPrint: CardToPrint): void {
    this.getSizesIfNotAvailable(cardToPrint.product);
    this.queryService.getProduct(cardToPrint.product.id).subscribe(result => {
      const newProduct = result;
      if (cardToPrint.sizes.length > 0) {
        // Verschillende lengtes toevoegen.
        for (const size_id of cardToPrint.sizes) {
          const cp = new CartProduct(newProduct);
          cp.size_id = size_id;
          this.cartProducts.push(cp);
          this.updateCart(this.cartProducts);
        }
      } else {
        this.addProduct(newProduct);
      }
    });
  }

  duplicateCartProduct(cp: CartProduct) {
    // Define location for new cartProduct
    let index: number = this.cartProducts.indexOf(cp) + 1;
    // Create new CP
    let newCP: CartProduct = new CartProduct(cp.product, cp);
    // Push new cp at correct location
    this.cartProducts.splice(index, 0, newCP);
    this.updateCart(this.cartProducts);
  }

  removeProduct(product: Product): void {
    // Removes all cartproducts that contain this specific product
    const newCartProductsList = this.cartProducts.filter(cp => cp.product_id !== product.id);
    this.cartProducts = newCartProductsList;
    this.updateCart(this.cartProducts);
  }

  removeCartProduct(toBeRemovedCp: CartProduct) {
    const newCartProductsList = this.cartProducts.filter(cp => cp !== toBeRemovedCp);
    this.deleteNotificationFromCard(toBeRemovedCp.product).subscribe();
    this.cartProducts = newCartProductsList;
    this.updateCart(this.cartProducts);
  }

  deleteNotificationFromCard(product: Product): Observable<any> {
    const url = '/communications/notifications/product/';
    const data = {'product_id': product.id, 'action': 'deleted_from_cart'};
    return this.queryService.patchQuery(url, JSON.stringify(data));
  }

  downloadCards(payload: string): Observable<any> {
    const printUrl = '/cards/queue/';
    this.resetCart();
    return this.queryService.postQuery(printUrl, payload);
  }

  closeDownloadsList() {
    this.showDownloads = false;
    this.downloads = [];
  }

  getProducts(): CartProduct[] {
    return this.cartProducts;
  }

  getNumberOfProductsInCart(): number {
    return this.cartProducts.length;
  }

  hasProducts(): boolean {
    return (this.cartProducts.length > 0);
  }

  pullDownAmount(): void {
    if (this.cartProducts.length > 1) {
      let amount = this.cartProducts[0].amount;
      for (let cp of this.cartProducts) {
        cp.amount = amount;
      }
    }
  }

  pullDownSize(silent:boolean = false): void {
    // Only execute the following code if we have multiple cartProducts
    if (this.cartProducts.length > 1) {
      // Size id that we want to copy to other cards
      let to_be_copied_size_id = this.cartProducts[0].size_id;
      let counter:number = -1;
      for (let cp of this.cartProducts) {
        let sizes = this.sizes.get(cp.product.id);
        // Only copy this size ID, if it is available
        let size_from_other_product = sizes.find(size_value => size_value.size_id == to_be_copied_size_id);
        if (size_from_other_product != undefined) {
          cp.size_id = to_be_copied_size_id;
          cp.template_options = size_from_other_product.cards;
          counter = counter + 1;
        }
      }
      if (!silent) {
        let message = 'This size is not available for other cards in your cart.';
        if (counter == 1) { message = 'Copied size to 1 other card in your cart.'}
        if (counter > 1) { message = 'Copied size to ' + counter + ' other cards in your cart.'}
        this.appNotificationService.addGrowl(message);
      }
    }
  }

  pullDownTemplate(silent:boolean = false): void {
    if (this.cartProducts.length > 1) {
      this.pullDownSize(true);
      let options_from_to_be_copied_product = this.cartProducts[0].template_options;
      let template_id = this.cartProducts[0].template_id;
      let index_to_be_copied = options_from_to_be_copied_product.findIndex((value, index) =>
        Number(value.id) == Number(template_id));
      let counter: number = -1;
      // Templates are copied based on the order of the selected template.
      // If our 'to-be-copied' template is the 3rd selected option, we will copy each 3rd template option for each
      // cartProduct. If a cartProduct has less options, we do nothing.
      for (let cp of this.cartProducts) {
        if (cp.template_options.length >= index_to_be_copied + 1) {
          let to_be_copied_template = cp.template_options[index_to_be_copied];
          cp.template_id = Number(to_be_copied_template.id);
          cp.template = to_be_copied_template;
          cp.allow_local_price = to_be_copied_template.allow_local_price;
          cp.require_local_price = to_be_copied_template.require_local_price;
          cp.require_store_price = to_be_copied_template.require_store_price;
          counter = counter + 1;
        }
        if (!silent) {
         let message = 'This theme is not available for other cards in your cart.';
         if (counter == 1) { message = 'Copied theme to 1 other card in your cart.'}
         if (counter > 1) { message = 'Copied theme to ' + counter + ' other cards in your cart.'}
         this.appNotificationService.addGrowl(message);
       }
      }
    }
  }
}




// WEBPACK FOOTER //
// /app/src/app/store/cart/cart.service.ts